"use client";

import { useState } from "react";
import Link from "next/link";
import { AlertTriangle, CheckCircle2, Loader2, Send } from "lucide-react";
import { submitGalleryReportAction } from "@/actions/reportActions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const REASONS = [
  ["sexual_explicit", "Contenido sexual explícito"],
  ["violence", "Violencia o contenido gráfico"],
  ["hate_or_harassment", "Odio o acoso"],
  ["impersonation", "Suplantación de identidad"],
  ["spam_or_scam", "Spam o intento de fraude"],
  ["other", "Otro incumplimiento"],
] as const;

function usernameFromInput(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;
  try {
    const candidate = raw.includes("://") ? new URL(raw) : new URL(`https://${raw}`);
    const sitePath = candidate.pathname.match(/^\/site\/([a-z0-9_-]+)\/?$/i);
    if (sitePath) return sitePath[1].toLowerCase();
    const firstHostPart = candidate.hostname.split(".")[0];
    if (firstHostPart && firstHostPart !== "www" && /^[a-z0-9_-]+$/i.test(firstHostPart)) return firstHostPart.toLowerCase();
  } catch {
    // Si no es una URL completa, se intenta como username debajo.
  }
  const username = raw.replace(/^@/, "").replace(/^\/|\/$/g, "");
  return /^[a-z0-9_-]{1,100}$/i.test(username) ? username.toLowerCase() : null;
}

export default function ReportForm() {
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    const username = usernameFromInput(target);
    if (!username) {
      setMessage({ type: "error", text: "Pega una URL de SnapSite o escribe un nombre de usuario válido." });
      return;
    }
    if (!reason) {
      setMessage({ type: "error", text: "Selecciona el motivo del reporte." });
      return;
    }
    setLoading(true);
    const result = await submitGalleryReportAction(username, reason, details);
    setLoading(false);
    setMessage({ type: result.success ? "success" : "error", text: result.message });
    if (result.success) {
      setTarget("");
      setReason("");
      setDetails("");
    }
  }

  return (
    <Card className="mt-6 overflow-hidden shadow-lg">
      <CardHeader className="border-b bg-muted/20">
        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        </div>
        <CardTitle>Reportar contenido</CardTitle>
        <CardDescription>
          Usa este formulario si una galería pública de SnapSite incumple nuestras reglas. Revisaremos los reportes y ocultaremos la galería mientras se investiga cuando sea necesario.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="report-target">Enlace o nombre de usuario</Label>
            <Input id="report-target" value={target} onChange={event => setTarget(event.target.value)} placeholder="https://usuario.snapsite.cc" autoComplete="off" required />
            <p className="text-xs text-muted-foreground">También puedes escribir solo el username, sin @.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="report-reason">Motivo</Label>
            <select id="report-reason" value={reason} onChange={event => setReason(event.target.value)} required className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">Selecciona un motivo</option>
              {REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="report-details">Detalles opcionales</Label>
            <Textarea id="report-details" value={details} onChange={event => setDetails(event.target.value.slice(0, 500))} maxLength={500} rows={4} placeholder="Cuéntanos qué encontraste y dónde aparece." />
            <p className="text-right text-xs text-muted-foreground">{details.length}/500</p>
          </div>
          {message && <div role="status" className={`flex items-start gap-2 rounded-md border p-3 text-sm ${message.type === "success" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-destructive/30 bg-destructive/10 text-destructive"}`}>
            {message.type === "success" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
            <span>{message.text}</span>
          </div>}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">Los reportes repetidos del mismo visitante se agrupan para evitar abusos.</p>
            <Button type="submit" disabled={loading} className="shrink-0">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Enviar reporte
            </Button>
          </div>
        </form>
        <p className="mt-6 text-center text-xs text-muted-foreground">¿Tienes otra consulta? <Link href="mailto:snapsite@rtsi.mx" className="text-primary hover:underline">Contacta a soporte</Link>.</p>
      </CardContent>
    </Card>
  );
}
